import { useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import {
  Zap,
  Battery,
  Clock,
  Calendar,
  ChevronRight,
  Loader2,
  TrendingUp,
  TrendingDown,
  FileText,
} from 'lucide-react';
import { useUnifiedTransactionStore } from '../../store/unifiedTransactionStore';
import { invoiceService } from '../../services/invoiceService';
import { UnifiedTransaction, TransactionFilterType } from '../../types';
import { BalanceIcon } from './icons/BalanceIcon';
import { formatBalance } from '../../utils/balanceFormatter';
import { PullToRefreshContainer } from './PullToRefreshContainer';

function ActivityShimmer() {
  return (
    <div className="space-y-3">
      {[...Array(5)].map((_, i) => (
        <div
          key={i}
          className="relative overflow-hidden bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-100 dark:border-gray-700"
        >
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700" />
              <div>
                <div className="h-4 w-20 bg-gray-200 dark:bg-gray-700 rounded mb-1.5" />
                <div className="h-3 w-14 bg-gray-200 dark:bg-gray-700 rounded" />
              </div>
            </div>
            <div className="w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded" />
          </div>
          <div className="h-4 w-3/4 bg-gray-200 dark:bg-gray-700 rounded mb-3" />
          <div className="pt-3 border-t border-gray-100 dark:border-gray-700 flex items-center gap-4">
            <div>
              <div className="h-3 w-10 bg-gray-200 dark:bg-gray-700 rounded mb-1" />
              <div className="h-5 w-16 bg-gray-200 dark:bg-gray-700 rounded" />
            </div>
            <div className="w-px h-8 bg-gray-200 dark:bg-gray-700" />
            <div>
              <div className="h-3 w-10 bg-gray-200 dark:bg-gray-700 rounded mb-1" />
              <div className="h-5 w-16 bg-gray-200 dark:bg-gray-700 rounded" />
            </div>
          </div>
          <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-white/20 dark:via-white/5 to-transparent" />
        </div>
      ))}
    </div>
  );
}

interface ActivityViewProps {
  onTransactionClick: (transaction: UnifiedTransaction) => void;
}

// Format date for display
const formatDate = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  // Check if date is valid
  if (isNaN(dateObj.getTime())) {
    return 'N/A';
  }

  return dateObj.toLocaleString('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Get transaction icon and color based on type
const getTransactionIcon = (type: string) => {
  switch (type) {
    case 'wallet_credit':
      return { Icon: TrendingUp, color: 'text-green-500 dark:text-green-400' };
    case 'wallet_debit':
      return { Icon: TrendingDown, color: 'text-red-500 dark:text-red-400' };
    case 'charging_session':
      return { Icon: Zap, color: 'text-blue-500 dark:text-blue-400' };
    default:
      return { Icon: Zap, color: 'text-gray-500 dark:text-gray-400' };
  }
};

// Get amount color based on transaction type
const getAmountColor = (type: string) => {
  switch (type) {
    case 'wallet_credit':
      return 'text-green-600 dark:text-green-400';
    case 'wallet_debit':
      return 'text-red-600 dark:text-red-400';
    case 'charging_session':
      return 'text-blue-600 dark:text-blue-400';
    default:
      return 'text-gray-900 dark:text-white';
  }
};

// Filter options (Charging first as default)
const filterOptions: { value: TransactionFilterType; label: 'Charging' | 'Credits' | 'Debits' }[] = [
  { value: 'charging', label: 'Charging' },
  { value: 'credits', label: 'Credits' },
  { value: 'debits', label: 'Debits' },
];

export function ActivityView({ onTransactionClick }: ActivityViewProps) {
  // Get data from unified transaction store
  const {
    unifiedTransactions,
    activeTransaction,
    filter,
    isLoading,
    error,
    currentPage,
    hasMore,
    isLoadingMore,
    fetchUnifiedTransactions,
    loadMoreUnifiedTransactions,
    refreshUnifiedTransactions,
    setFilter,
  } = useUnifiedTransactionStore();

  // Local state for invoice download
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState<string | null>(null);
  const [invoiceError, setInvoiceError] = useState<string | null>(null);

  // Fetch unified transactions on mount
  useEffect(() => {
    fetchUnifiedTransactions(1);
  }, [fetchUnifiedTransactions]);

  // Infinite scroll implementation
  const observerTarget = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const target = observerTarget.current;
    const scrollContainer = scrollContainerRef.current;
    if (!target || !scrollContainer) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // When the sentinel element is visible, load more
        if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
          console.log('[InfiniteScroll] Loading more unified transactions...', { currentPage, hasMore });
          loadMoreUnifiedTransactions();
        }
      },
      {
        root: scrollContainer, // Observe intersections within the scrollable container
        threshold: 0.1, // Trigger when 10% visible
        rootMargin: '100px', // Start loading 100px before reaching bottom
      }
    );

    observer.observe(target);

    return () => {
      if (target) {
        observer.unobserve(target);
      }
    };
  }, [hasMore, isLoadingMore, loadMoreUnifiedTransactions, currentPage]);

  // Handle invoice download for wallet recharges
  const handleDownloadInvoice = async (transaction: UnifiedTransaction) => {
    if (!transaction.walletTransaction) return;

    setDownloadingInvoiceId(transaction.id);
    setInvoiceError(null);

    try {
      const walletTransactionId = transaction.walletTransaction.id;
      const invoice = await invoiceService.getOrGenerateInvoice(walletTransactionId);

      // Open PDF in new tab
      window.open(invoice.pdfUrl, '_blank');
    } catch (error: any) {
      console.error('[ActivityView] Failed to download invoice:', error);
      Sentry.captureException(error, { tags: { feature: 'invoice_download' } });
      setInvoiceError('Failed to download invoice. Please try again.');
    } finally {
      setDownloadingInvoiceId(null);
    }
  };

  // Handle filter change
  const handleFilterChange = (newFilter: TransactionFilterType) => {
    setFilter(newFilter);
  };

  return (
    <div ref={scrollContainerRef} className="h-full bg-gray-50 dark:bg-gray-900 overflow-auto pb-24 transition-colors">
      <PullToRefreshContainer
        onRefresh={refreshUnifiedTransactions}
        scrollContainerRef={scrollContainerRef}
      >
      <div className="px-4 py-6">
        {/* Page Title with Logo */}
        <div className="mb-6 flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
            <svg width="40" height="40" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="50" cy="50" r="45" stroke="#8bc43e" strokeWidth="7"/>
              <rect x="32" y="30" width="10" height="20" rx="1" fill="#8bc43e"/>
              <rect x="58" y="30" width="10" height="20" rx="1" fill="#8bc43e"/>
              <rect x="41" y="58" width="18" height="12" rx="1" fill="#8bc43e"/>
            </svg>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Activity</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">Transaction history</p>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="mb-6 flex gap-2 overflow-x-auto">
          {filterOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => handleFilterChange(option.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                filter === option.value
                  ? 'bg-[#8bc43e] text-white shadow-md'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        {/* Invoice Error Alert */}
        {invoiceError && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-600 dark:text-red-400">{invoiceError}</p>
          </div>
        )}

        {/* Transactions Header */}
        <div className="mb-4">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
            {filter === 'charging' ? 'Charging Sessions' :
             filter === 'credits' ? 'Credits' :
             filter === 'debits' ? 'Debits' : ''}
          </h3>
        </div>

        {/* Loading State - Shimmer skeleton for initial load */}
        {isLoading && !isLoadingMore && unifiedTransactions.length === 0 && (
          <ActivityShimmer />
        )}

        {/* Error State */}
        {error && unifiedTransactions.length === 0 && (
          <div className="text-center py-12">
            <p className="text-red-500 dark:text-red-400 mb-2">Failed to load transactions</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">{error}</p>
            <button
              onClick={() => fetchUnifiedTransactions(1)}
              className="mt-4 px-4 py-2 bg-[#8bc43e] text-white rounded-lg text-sm font-medium"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Transaction List */}
        {!isLoading && !error && unifiedTransactions.length > 0 && (
          <div className="space-y-3">
            {unifiedTransactions.map((transaction) => {
              const { Icon, color } = getTransactionIcon(transaction.type);
              const amountColor = getAmountColor(transaction.type);
              const isWalletTransaction = transaction.type === 'wallet_credit' || transaction.type === 'wallet_debit';
              const isDebitWithChargingLink = transaction.type === 'wallet_debit' && transaction.linkToCharging;
              const isClickable = !isWalletTransaction || isDebitWithChargingLink;
              const isActiveCharging = transaction.chargingTransaction?.isActive;

              return (
                <div
                  key={transaction.id}
                  className="relative w-full bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-100 dark:border-gray-700"
                >
                  {/* LIVE Badge for active charging sessions */}
                  {isActiveCharging && (
                    <div className="absolute top-2 right-2 z-10">
                      <div className="flex items-center gap-1 bg-[#8bc43e] text-white text-xs px-2 py-1 rounded-full font-medium">
                        <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                        LIVE
                      </div>
                    </div>
                  )}

                  {/* Non-clickable wallet transactions (credits or debits without charging link) */}
                  {!isClickable ? (
                    <div className="w-full">
                      {/* Header with Icon and Date */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            transaction.type === 'wallet_credit' ? 'bg-green-100 dark:bg-green-900/20' :
                            transaction.type === 'wallet_debit' ? 'bg-red-100 dark:bg-red-900/20' :
                            'bg-blue-100 dark:bg-blue-900/20'
                          }`}>
                            <Icon className={`w-5 h-5 ${color}`} />
                          </div>
                          <div className="text-left">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {formatDate(transaction.date).split(', ')[0]}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {formatDate(transaction.date).split(', ')[1]}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Description */}
                      <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">{transaction.description}</p>

                      {/* Transaction Details */}
                      <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-700">
                        <div className="flex items-center gap-4">
                          {/* Amount */}
                          <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Amount</p>
                            <p className={`font-bold ${amountColor} flex items-center gap-1`}>
                              {transaction.type === 'wallet_credit' ? '+' : '-'}<BalanceIcon className="w-3.5 h-3.5" />{formatBalance(transaction.amount)}
                            </p>
                          </div>

                          {/* Balance for wallet transactions */}
                          {typeof transaction.balanceAfter === 'number' && !isNaN(transaction.balanceAfter) && (
                            <>
                              <div className="w-px h-8 bg-gray-200 dark:bg-gray-700"></div>
                              <div>
                                <p className="text-xs text-gray-500 dark:text-gray-400">Balance</p>
                                <p className="font-semibold text-gray-900 dark:text-white flex items-center gap-1">
                                  <BalanceIcon className="w-3.5 h-3.5" />{formatBalance(transaction.balanceAfter)}
                                </p>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => onTransactionClick(transaction)}
                      className="w-full text-left"
                    >
                      {/* Header with Icon and Date */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            transaction.type === 'wallet_credit' ? 'bg-green-100 dark:bg-green-900/20' :
                            transaction.type === 'wallet_debit' ? 'bg-red-100 dark:bg-red-900/20' :
                            'bg-blue-100 dark:bg-blue-900/20'
                          }`}>
                            <Icon className={`w-5 h-5 ${color}`} />
                          </div>
                          <div className="text-left">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {formatDate(transaction.date).split(', ')[0]}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {formatDate(transaction.date).split(', ')[1]}
                            </p>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-300 dark:text-gray-600 flex-shrink-0" />
                      </div>

                      {/* Description */}
                      <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">{transaction.description}</p>

                      {/* Transaction Details */}
                      <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-700">
                        <div className="flex items-center gap-4">
                          {/* Amount - no +/- for charging sessions, show - for debits */}
                          <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Amount</p>
                            <p className={`font-bold ${amountColor} flex items-center gap-1`}>
                              {isDebitWithChargingLink ? '-' : ''}<BalanceIcon className="w-3.5 h-3.5" />{formatBalance(transaction.amount)}
                            </p>
                          </div>

                          {/* Energy for charging sessions */}
                          {typeof transaction.energy === 'number' && !isNaN(transaction.energy) && (
                            <>
                              <div className="w-px h-8 bg-gray-200 dark:bg-gray-700"></div>
                              <div>
                                <p className="text-xs text-gray-500 dark:text-gray-400">Energy</p>
                                <p className="font-semibold text-gray-900 dark:text-white">
                                  {transaction.energy.toFixed(2)} kWh
                                </p>
                              </div>
                            </>
                          )}

                          {/* Balance for debit transactions with charging link */}
                          {isDebitWithChargingLink && typeof transaction.balanceAfter === 'number' && !isNaN(transaction.balanceAfter) && (
                            <>
                              <div className="w-px h-8 bg-gray-200 dark:bg-gray-700"></div>
                              <div>
                                <p className="text-xs text-gray-500 dark:text-gray-400">Balance</p>
                                <p className="font-semibold text-gray-900 dark:text-white flex items-center gap-1">
                                  <BalanceIcon className="w-3.5 h-3.5" />{formatBalance(transaction.balanceAfter)}
                                </p>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </button>
                  )}

                  {/* Invoice Download Button for wallet recharges */}
                  {transaction.canDownloadInvoice && (
                    <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownloadInvoice(transaction);
                        }}
                        disabled={downloadingInvoiceId === transaction.id}
                        className="flex items-center gap-2 text-sm text-[#8bc43e] hover:text-[#6fa02e] disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {downloadingInvoiceId === transaction.id ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span>Downloading...</span>
                          </>
                        ) : (
                          <>
                            <FileText className="w-4 h-4" />
                            <span>Download Invoice</span>
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Infinite Scroll Sentinel - triggers loading more */}
            <div ref={observerTarget} className="h-4" />

            {/* Loading More Indicator */}
            {isLoadingMore && (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="w-6 h-6 text-[#8bc43e] animate-spin" />
                <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                  Loading more transactions...
                </span>
              </div>
            )}

            {/* No More Transactions Message */}
            {!isLoadingMore && !hasMore && (
              <div className="text-center py-6">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No more transactions
                </p>
              </div>
            )}
          </div>
        )}

        {/* Empty State for no transactions */}
        {!isLoading && !error && unifiedTransactions.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <Zap className="w-8 h-8 text-gray-400 dark:text-gray-500" />
            </div>
            <p className="text-gray-500 dark:text-gray-400">
              {filter === 'charging' ? 'No charging sessions yet' :
               filter === 'credits' ? 'No credits found' :
               filter === 'debits' ? 'No debits found' : ''}
            </p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
              Try a different filter
            </p>
          </div>
        )}
      </div>
      </PullToRefreshContainer>
    </div>
  );
}
